import { useState, type ComponentType, type ReactNode } from 'react';
import { Sparkles, X } from 'lucide-react';

// A soft brand-tinted intro card with a dismiss button. The dismissal is
// remembered per `storageKey` in localStorage so it stays gone on reload.
// Used for the once-off "what is this page" blurbs on the overview and section
// pages — keep new instances pointing at a distinct storageKey.
export default function DismissableInfoCard({
  storageKey,
  title,
  icon: Icon = Sparkles,
  children,
}: {
  storageKey: string;
  title: string;
  icon?: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(storageKey) === '1',
  );

  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
  };

  return (
    <div className="bg-brand/5 border border-brand/15 rounded-xl p-5 mb-6 flex items-start gap-3.5">
      <div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-graphite mb-1">{title}</h4>
        <p className="text-[13px] text-coolslate leading-relaxed">{children}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 -m-1 text-coolslate hover:text-graphite transition-colors cursor-pointer shrink-0"
        title="Dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
